// Styles
import { makeStyles, tokens } from "@fluentui/react-components";
import { flexParent, flexParentCenterAll, flexParentCenterCrossAxis, responsiveBreakpoint, styleScrollbar, textClip } from "@ms/planner-generic-controls/lib/local/utilities/Mixin.styles";
const DIALOG_MAX_WIDTH = "600px";
const DIALOG_DEFAULT_HEIGHT = "520px";
const DIALOG_MAX_HEIGHT = "720px";
const TILE_PADDING = tokens.spacingVerticalM;
const GROUP_ICON_SIZE = tokens.spacingVerticalXXXL;
/**
 * Styles for SharePlanDialog
 */ export const useSharePlanDialogStyles = makeStyles({
    surface: {
        maxWidth: DIALOG_MAX_WIDTH,
        width: "70%",
        minHeight: `min(${DIALOG_DEFAULT_HEIGHT}, 90vh)`,
        maxHeight: DIALOG_MAX_HEIGHT,
        "@media (max-height: 400px)": {
            maxHeight: "90vh"
        }
    },
    dialogTitle: {
        ...textClip("ellipsis"),
        flexShrink: 0,
        ...responsiveBreakpoint("small", {
            whiteSpace: "normal",
            overflow: "visible",
            textOverflow: "clip",
            wordBreak: "break-word"
        })
    },
    body: {
        gap: tokens.spacingVerticalXL,
        ...responsiveBreakpoint("small", {
            gap: tokens.spacingVerticalM
        })
    },
    content: {
        ...styleScrollbar,
        maxHeight: "598px",
        overflowY: "auto",
        overflowX: "hidden",
        flex: "1 1 auto",
        minHeight: 0,
        "@media (max-height: 400px)": {
            maxHeight: "50vh"
        }
    },
    button: {
        borderRadius: tokens.borderRadiusXLarge,
        minWidth: "auto"
    },
    subheading: {
        margin: "0"
    },
    loadingText: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorBrandForeground1,
        fontWeight: tokens.fontWeightRegular
    },
    groupDisabledContainer: {
        display: "flex",
        ...flexParentCenterAll,
        height: "170px"
    },
    groupDisabledText: {
        width: "90%"
    },
    statusView: {
        ...flexParent("column"),
        ...flexParentCenterAll,
        height: "240px"
    },
    errorView: {
        ...flexParent("column"),
        ...flexParentCenterAll
    },
    footer: {
        width: "100%",
        display: "flex",
        justifyContent: "flex-end",
        marginTop: tokens.spacingVerticalL,
        flex: "0 0 auto",
        ...responsiveBreakpoint("small", {
            flexWrap: "wrap",
            gap: tokens.spacingHorizontalS,
            marginTop: tokens.spacingVerticalM
        })
    },
    footerContent: {
        display: "flex",
        width: "100%",
        justifyContent: "space-between",
        // Stack footer buttons vertically on small screens
        ...responsiveBreakpoint("small", {
            flexDirection: "column",
            gap: tokens.spacingVerticalS
        })
    },
    tileContainer: {
        ...flexParent("column", "nowrap")
    },
    teamsContainer: {
        marginBottom: tokens.spacingVerticalM
    },
    groupTile: {
        ...flexParent("column", "nowrap"),
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusXLarge
    },
    groupTileHeader: {
        ...flexParent("row", "nowrap"),
        ...flexParentCenterCrossAxis,
        justifyContent: "space-between",
        backgroundColor: "transparent",
        border: "none",
        width: "100%",
        padding: TILE_PADDING,
        height: "64px",
        cursor: "pointer",
        color: "unset",
        // Allow wrapping on small screens for better reflow
        ...responsiveBreakpoint("small", {
            flexWrap: "wrap",
            height: "auto",
            minHeight: "64px",
            padding: tokens.spacingVerticalS
        })
    },
    groupTileHeaderDisabled: {
        cursor: "unset"
    },
    groupPersona: {
        ...flexParent("column", "nowrap"),
        alignItems: "flex-start",
        // Allow persona to shrink and wrap on small screens
        ...responsiveBreakpoint("small", {
            flex: "1 1 auto",
            minWidth: "0"
        })
    },
    groupPersonaTitle: {
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase300,
        // Allow title to wrap on small screens
        ...responsiveBreakpoint("small", {
            wordBreak: "break-word"
        })
    },
    groupPersonaSubtitle: {
        fontWeight: tokens.fontWeightRegular,
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
        // Allow subtitle to wrap on small screens
        ...responsiveBreakpoint("small", {
            wordBreak: "break-word"
        })
    },
    groupTitleInfo: {
        ...textClip("ellipsis"),
        ...flexParent("row", "nowrap"),
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase300,
        alignItems: "center",
        // Allow text to wrap on very small screens instead of truncating
        ...responsiveBreakpoint("small", {
            whiteSpace: "normal",
            overflow: "visible",
            textOverflow: "clip",
            wordBreak: "break-word"
        })
    },
    groupTileIcon: {
        ...flexParent(),
        ...flexParentCenterAll,
        width: GROUP_ICON_SIZE,
        height: GROUP_ICON_SIZE,
        opacity: "1",
        borderRadius: tokens.borderRadiusSmall,
        marginRight: TILE_PADDING
    },
    label: {
        marginBottom: tokens.spacingVerticalXXS,
        paddingTop: tokens.spacingVerticalXXS,
        paddingBottom: tokens.spacingVerticalXXS
    },
    rosterContainer: {
        paddingBottom: tokens.spacingVerticalS
    },
    primaryErrorText: {
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase400
    },
    secondaryErrorText: {
        fontWeight: tokens.fontWeightRegular,
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground2
    }
});
/**
 * Styles for the People/Agent TabList in SharePlanDialog
 */ export const useDialogTabListStyles = makeStyles({
    root: {
        paddingBottom: "0px",
        paddingTop: tokens.spacingVerticalS,
        borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralBackground3Pressed}`
    },
    tab: {
        color: tokens.colorNeutralForeground2,
        columnGap: tokens.spacingHorizontalXS,
        // Selected state styles
        "&[aria-selected='true']": {
            "::after": {
                height: tokens.strokeWidthThin,
                left: "1px",
                right: "1px"
            },
            "*": {
                fontWeight: tokens.fontWeightBold
            }
        },
        ":hover": {
            "::before": {
                height: tokens.strokeWidthThin
            }
        }
    },
    tabText: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2
    }
});
